import { useCallback } from "react";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "components/UI/Tooltip";
import { useCesiumContext } from "hooks/useCesiumContext";
import { useEditMode } from "hooks/useEditMode";
import { useMapContext } from "hooks/useMapContext";
import { useUserWaypoints } from "hooks/useUserWaypoints";
import { useTranslation } from "react-i18next";
import { useRouting } from "stores/routingStore/RoutingContext";
import { fitTrackToScreen, fitWaypointToScreen } from "utils/map/fitToScreen";
import { CollapsibleTopMenuActionIcon } from "../../../../components/UI/TopMenu/CollapsibleActionIcon";

export const FitToScreenIcon = ({ isOpen }: { isOpen: boolean }) => {
  const { map } = useMapContext();
  const {
    state: { computedTrackpoints, trackWaypoints },
  } = useRouting();
  const { editMode } = useEditMode();
  const { selectedWaypoint } = useUserWaypoints();
  const { t } = useTranslation();
  const { cesium } = useCesiumContext();

  const fitToScreen = useCallback(() => {
    if (map) {
      if (editMode === "track") {
        if (trackWaypoints.length === 1) {
          map.setView({
            lat: trackWaypoints[0][0],
            lng: trackWaypoints[0][1],
          });
        } else {
          fitTrackToScreen(computedTrackpoints, map);
        }
      }
      if (editMode === "waypoint" && selectedWaypoint) {
        fitWaypointToScreen(selectedWaypoint.point, map);
      }
    } else if (cesium) {
      // Cesium is loaded lazily together with the 3D map, keep it out of the main bundle
      void import("utils/cesium/flyToCoords").then(({ flyToCoords }) =>
        flyToCoords(cesium, computedTrackpoints)
      );
    }
  }, [
    cesium,
    computedTrackpoints,
    editMode,
    map,
    selectedWaypoint,
    trackWaypoints,
  ]);

  return (
    <Tooltip
      label={
        selectedWaypoint
          ? t("editmode.fit_to_screen_button.fit_waypoint_to_view_tooltip")
          : t("editmode.fit_to_screen_button.fit_track_to_view_tooltip")
      }
    >
      <div>
        <CollapsibleTopMenuActionIcon isOpen={isOpen} onClick={fitToScreen}>
          <FontAwesomeIcon icon={faExpand} />
        </CollapsibleTopMenuActionIcon>
      </div>
    </Tooltip>
  );
};
